import type { RouteRecordRaw } from 'vue-router'
import IdpLayout from '../../components/layout/IdpLayout.vue'
import SidePanel from '../../components/layout/SidePanel.vue'
import { requireAuth, requireCostExplorerViewer } from '../middleware/auth'

// All page components are lazy-loaded: modules only parse & execute when the
// user actually navigates to that route, preventing eagerly triggered store
// initialisations, API calls, and bundle-parse costs on app startup.
const lazy = <T>(fn: () => Promise<T>) => fn

export const mainRoutes: RouteRecordRaw[] = [
  {
    path: '/idp',
    component: IdpLayout,
    beforeEnter: requireAuth,
    children: [
      { path: '', component: lazy(() => import('../../pages/idp/landing.vue')) },
      {
        path: 'services',
        component: lazy(() => import('../../pages/idp/index.vue'))
      },
      {
        path: 'team',
        component: lazy(() => import('../../pages/idp/team.vue'))
      },
      {
        path: 'subteam',
        component: lazy(() => import('../../pages/idp/subteam.vue'))
      },
      {
        path: 'workspace',
        component: lazy(() => import('../../pages/idp/workspace.vue'))
      },
      {
        path: 'graph',
        component: lazy(() => import('../../pages/idp/graph.vue'))
      },
      {
        path: 'packages',
        component: lazy(() => import('../../pages/idp/packages.vue'))
      }
    ]
  },
  {
    path: '/',
    component: SidePanel,
    beforeEnter: requireAuth,
    children: [
      { path: '', component: lazy(() => import('../../pages/Landing.vue')) },
      {
        path: '/request-filter',
        component: lazy(() => import('../../pages/request-filter/index.vue'))
      },
      {
        path: '/workers-filter',
        component: lazy(() => import('../../pages/workers-filter/index.vue'))
      },
      { path: '/iam-config', component: lazy(() => import('../../pages/iam-config/index.vue')) },
      { path: '/iam-sandbox', component: lazy(() => import('../../pages/iam-sandbox/index.vue')) },
      {
        path: '/iam-auth-audit',
        component: lazy(() => import('../../pages/iam-auth-audit/index.vue'))
      },
      { path: '/audit-logs', component: lazy(() => import('../../pages/audit-logs/index.vue')) },
      { path: '/database', component: lazy(() => import('../../pages/database/index.vue')) },
      {
        path: '/database/firestore',
        component: lazy(() => import('../../pages/database/firestore.vue'))
      },
      { path: '/database/mongo', component: lazy(() => import('../../pages/database/mongo.vue')) },
      {
        path: '/database/db-query',
        component: lazy(() => import('../../pages/database/db-query/index.vue'))
      },
      // ── Atlas Intel (Mongo) ───────────────────────────────────────────────
      {
        path: '/database/atlas-intel',
        component: lazy(() => import('../../pages/database/atlas-intel/index.vue'))
      },
      {
        path: '/database/atlas-intel/costs',
        component: lazy(() => import('../../pages/database/atlas-intel/costs.vue'))
      },
      {
        path: '/database/atlas-intel/storage',
        component: lazy(() => import('../../pages/database/atlas-intel/storage.vue'))
      },
      {
        path: '/database/atlas-intel/indexes',
        component: lazy(() => import('../../pages/database/atlas-intel/indexes.vue'))
      },
      {
        path: '/database/atlas-intel/ops',
        component: lazy(() => import('../../pages/database/atlas-intel/ops.vue'))
      },
      {
        path: '/database/atlas-intel/recommendations',
        component: lazy(() => import('../../pages/database/atlas-intel/recommendations.vue'))
      },
      {
        path: '/database/atlas-intel/health',
        component: lazy(() => import('../../pages/database/atlas-intel/health.vue'))
      },
      {
        path: '/database/atlas-intel/atlas-ai-history',
        component: lazy(() => import('../../pages/database/atlas-intel/atlas-ai-history.vue'))
      },
      // meta.requiresAdmin: guard checked in router/index.ts beforeEach — restrict to admin users
      {
        path: '/database/atlas-intel/admin',
        component: lazy(() => import('../../pages/database/atlas-intel/admin.vue')),
        meta: { requiresAdmin: true }
      },
      // ── Firestore Estimator (cost projection) ─────────────────────────────
      {
        path: '/database/firestore-estimator',
        component: lazy(() => import('../../pages/database/firestore-estimator/index.vue'))
      },
      // ── Firestore Intel (Key Visualizer derived) ─────────────────────────
      {
        path: '/database/firestore-intel/monotonic-indexes',
        component: lazy(() => import('../../pages/database/firestore-intel/monotonic-indexes.vue'))
      },
      // ── ClickHouse AI (chat / workload advisor / admin) ───────────────────
      {
        path: '/database/clickhouse-ai',
        component: lazy(() => import('../../pages/database/clickhouse-ai/index.vue'))
      },
      {
        path: '/database/clickhouse-ai/chat',
        component: lazy(() => import('../../pages/database/clickhouse-ai/chat.vue'))
      },
      {
        path: '/database/clickhouse-ai/workload',
        component: lazy(() => import('../../pages/database/clickhouse-ai/workload.vue'))
      },
      {
        path: '/database/clickhouse-ai/admin',
        component: lazy(() => import('../../pages/database/clickhouse-ai/admin.vue')),
        meta: { requiresAdmin: true }
      },
      {
        path: '/dependency-graph',
        component: lazy(() => import('../../pages/dependency-graph/index.vue'))
      },
      { path: '/packages', component: lazy(() => import('../../pages/package-view/index.vue')) },
      {
        path: '/packages/:packageName',
        component: lazy(() => import('../../pages/package-details/index.vue'))
      },
      {
        path: '/packages/:packageName/:version',
        component: lazy(() => import('../../pages/package-details/index.vue'))
      },
      {
        path: '/workload/:gitRepo/:workload/:environment',
        component: lazy(() => import('../../pages/workload-details/index.vue'))
      },
      {
        path: '/quality-gates',
        component: lazy(() => import('../../pages/quality-gates/index.vue'))
      },
      {
        path: '/quality-gates/overview',
        component: lazy(() => import('../../pages/quality-gates-overview/index.vue'))
      },
      {
        path: '/quality-gates/compliance',
        component: lazy(() => import('../../pages/quality-gates-leaderboard/index.vue'))
      },
      {
        path: '/quality-gates/compliance/details',
        component: lazy(() => import('../../pages/quality-gates-leaderboard/details.vue'))
      },
      {
        path: '/quality-gates/compliance/prs',
        component: lazy(() => import('../../pages/quality-gates-compliance-prs/index.vue'))
      },
      {
        path: '/quality-gates/:repo/:prNumber',
        component: lazy(() => import('../../pages/quality-gates/details.vue'))
      },
      {
        path: '/platform-report',
        component: lazy(() => import('../../pages/platform-report/index.vue'))
      },
      {
        path: '/platform-report/create',
        component: lazy(() => import('../../pages/platform-report/create.vue'))
      },
      {
        path: '/platform-report/edit/:id',
        component: lazy(() => import('../../pages/platform-report/edit.vue'))
      },
      {
        path: '/class-latency',
        component: lazy(() => import('../../pages/class-latency/index.vue'))
      },
      {
        path: '/api-tiering/reports',
        component: lazy(() => import('../../pages/api-tiering-reports/index.vue'))
      },
      { path: '/initiatives', component: lazy(() => import('../../pages/initiatives/index.vue')) },
      {
        path: '/initiatives/:trackerId',
        component: lazy(() => import('../../pages/initiatives/TrackerDetail.vue'))
      },
      // Dev-raised platform issues → ClickUp, tracked here so devs don't have to
      // open ClickUp to check status.
      {
        path: '/issues',
        component: lazy(() => import('../../pages/data-issues/index.vue'))
      },
      // Was /data-issues until the page was renamed to "Issues". Kept as a redirect:
      // ClickUp tickets raised before the rename carry the old link in their body.
      { path: '/data-issues', redirect: '/issues' },
      {
        path: '/task-sentinel',
        component: lazy(() => import('../../pages/task-sentinel/index.vue'))
      },
      { path: '/ts', component: lazy(() => import('../../pages/task-sentinel/index.vue')) },
      {
        path: '/task-sentinel/:id',
        component: lazy(() => import('../../pages/task-sentinel/details.vue'))
      },
      { path: '/releases', component: lazy(() => import('../../pages/releases/index.vue')) },
      {
        path: '/releases/create',
        component: lazy(() => import('../../pages/releases/create.vue'))
      },
      {
        path: '/releases/edit/:id',
        component: lazy(() => import('../../pages/releases/edit.vue'))
      },
      {
        path: '/manager-dashboard',
        component: lazy(() => import('../../pages/manager-dashboard/index.vue'))
      },
      {
        path: '/apps-inventory',
        component: lazy(() => import('../../pages/apps-inventory/index.vue'))
      },
      {
        path: '/operations-dashboard',
        component: lazy(() => import('../../pages/operations-dashboard/index.vue'))
      },
      {
        path: '/cost-explorer',
        component: lazy(() => import('../../pages/cost-explorer/index.vue')),
        beforeEnter: requireCostExplorerViewer
      },
      {
        path: '/cost-explorer/teams',
        component: lazy(() => import('../../pages/cost-explorer/TeamsPage.vue')),
        beforeEnter: requireCostExplorerViewer
      }
    ]
  }
]
